import { Link } from "react-router-dom";
import { LegalPage, Sec, type TocEntry } from "../components/LegalPage";
import { COMPANY, PLATFORM_LINKS } from "../lib/company";
import { EmailLink } from "../components/EmailLink";

const TOC: TocEntry[] = [
  { id: "intro", title: "Introduction" },
  { id: "collect", title: "Information We Collect" },
  { id: "connected", title: "Information From Connected Accounts" },
  { id: "tokens", title: "Access Tokens and Passwords" },
  { id: "media", title: "Media You Upload" },
  { id: "use", title: "How We Use Information" },
  { id: "meta", title: "Facebook and Instagram Data" },
  { id: "youtube", title: "YouTube API Services and Google Data" },
  { id: "google-retention", title: "Retention and Deletion of Google User Data" },
  { id: "cookies", title: "Cookies and Local Storage" },
  { id: "sharing", title: "Sharing of Information" },
  { id: "processors", title: "Service Providers and Hosting" },
  { id: "retention", title: "Data Retention" },
  { id: "deletion", title: "Deleting Your Data" },
  { id: "rights", title: "Your Rights" },
  { id: "security", title: "Security" },
  { id: "transfers", title: "International Transfers" },
  { id: "children", title: "Children" },
  { id: "changes", title: "Changes to This Policy" },
  { id: "contact", title: "Contact Us" },
  { id: "grievance", title: "Grievance Redressal" },
];

export function Privacy() {
  return (
    <div className="wrap wrap-legal">
      <LegalPage
        title="Privacy Policy"
        toc={TOC}
        intro={<>
          This Privacy Policy explains how {COMPANY.product} collects, uses, stores, discloses
          and protects personal information when you use our website, mobile application,
          scheduling tools, media library and social publishing services.
        </>}
      >
        <Sec id="intro" n={1} title="Introduction">
          <p>
            {COMPANY.product} lets you compose a post once, attach a photo or video, choose which
            of your connected Facebook Pages, Instagram Business accounts and YouTube channels
            should receive it, and publish immediately or at a scheduled time.
          </p>
          <p>
            In this policy, &ldquo;{COMPANY.product}&rdquo;, &ldquo;Company&rdquo;, &ldquo;we&rdquo;,
            &ldquo;us&rdquo; or &ldquo;our&rdquo; refers to {COMPANY.legalName}, which also owns
            the domain this site is served from. The product is made with{" "}
            <a href={COMPANY.studio.url} target="_blank" rel="noreferrer noopener">
              {COMPANY.studio.name} &mdash; {COMPANY.studio.tagline}
            </a>, the company&rsquo;s own product tooling rather than a third-party processor,
            so it receives none of the data described below.
            &ldquo;You&rdquo; means a visitor, account holder, workspace member or other user of
            the platform.
          </p>
          <p>
            Related documents: <Link to="/terms">Terms of Service</Link>,{" "}
            <Link to="/data-deletion">Delete your data</Link> and <Link to="/security">Security</Link>.
          </p>
        </Sec>

        <Sec id="collect" n={2} title="Information We Collect">
          <p><strong>Account information:</strong> the email address you sign in with, your user
            identifier, workspace name, workspace membership and role, and your communication
            preferences.</p>
          <p><strong>Content you create:</strong> post captions, titles, descriptions, scheduled
            publish times, target account selections, and the delivery status of each post.</p>
          <p><strong>Media you upload:</strong> the photo and video files you add to a post,
            together with technical facts we read from them such as file size, MIME type, image
            dimensions, video duration, aspect ratio and a content checksum.</p>
          <p><strong>Connection information:</strong> the identifiers, display names and profile
            images of the social accounts you connect, and the encrypted credentials described in
            section 4.</p>
          <p><strong>Device and usage data:</strong> browser and device type, operating system, IP
            address, pages viewed, session activity, and diagnostic or error records generated
            while you use the service.</p>
          <p><strong>Support information:</strong> messages, attachments and account details you
            send us when you contact support.</p>
        </Sec>

        <Sec id="connected" n={3} title="Information From Connected Accounts">
          <p>
            When you connect a social account, we receive only the information needed to list the
            account and publish on your behalf:
          </p>
          <ul>
            <li><strong>Facebook:</strong> the Pages you administer, and for each Page its Page ID,
              name, profile picture and a Page access token.</li>
            <li><strong>Instagram:</strong> the Instagram Business or Creator account linked to a
              Page you administer, and its account ID, username and profile picture.</li>
            <li><strong>YouTube:</strong> the channels on your Google account, and for each channel
              its channel ID, title and thumbnail.</li>
          </ul>
          <p>
            We also store the identifier and permalink that a platform returns after a successful
            publish, so we can show you where a post went and avoid publishing the same post twice.
          </p>
          <p>
            We do not read your inbox, direct messages, friends list, contact list, follower list or
            private posts. We do not download your existing photo or video library from any platform.
          </p>
        </Sec>

        <Sec id="tokens" n={4} title="Access Tokens and Passwords">
          <p>
            <strong>We never see, receive or store your Facebook, Instagram or Google password.</strong>{" "}
            When you connect an account, we send you to the platform&rsquo;s own website. You type
            your password there, on their domain, over their connection. The platform then returns
            an access token to us — a revocable key scoped to the specific permissions you approved.
          </p>
          <p>Those tokens are protected by four independent measures:</p>
          <ul>
            <li>They are encrypted with AES-256-GCM before being written to storage.</li>
            <li>The encryption key is held in our server-side function environment, never in the
              database, so a copy of the database alone does not reveal a usable token.</li>
            <li>They are stored in a private database schema that is not exposed through our public
              API and cannot be selected by any signed-in user, including you.</li>
            <li>Row-level security is enabled on that storage with no policy that grants access,
              so ordinary application credentials are refused even if the other layers were bypassed.</li>
          </ul>
          <p>
            Tokens are decrypted only inside the server-side function that is publishing your post,
            for the duration of that call. They are never sent to your browser or mobile app.
          </p>
          <p>
            You can disconnect an account inside {COMPANY.product} at any time, which deletes the
            stored token. You can also revoke our access directly from{" "}
            <a href={PLATFORM_LINKS.metaAppSettings} target="_blank" rel="noreferrer noopener">Facebook Business Integrations</a>{" "}
            or your{" "}
            <a href={PLATFORM_LINKS.googlePermissions} target="_blank" rel="noreferrer noopener">Google security settings</a>,
            without visiting our site at all.
          </p>
        </Sec>

        <Sec id="media" n={5} title="Media You Upload">
          <p>
            Photos and videos you upload are held in a private storage bucket. They are not public,
            not indexed and not listable. Access is restricted to the workspace that uploaded them.
          </p>
          <p>
            Facebook and Instagram publish media by fetching it from a URL rather than accepting a
            file upload. To publish to those platforms we generate a short-lived signed link to your
            file, valid for a limited period (two hours by default) and unguessable, and pass it to
            Meta. The link expires on its own; it is not reused and is not shared with anyone else.
          </p>
          <p>
            YouTube requires the file itself. Videos destined for YouTube are uploaded from your
            browser or mobile app directly to Google&rsquo;s upload endpoint. Those bytes do not pass
            through our servers.
          </p>
          <p>
            We do not run facial recognition, biometric analysis or content-based advertising
            profiling on your media.
          </p>
        </Sec>

        <Sec id="use" n={6} title="How We Use Information">
          <ul>
            <li>Create, authenticate and manage your account and workspace.</li>
            <li>Store drafts, media and schedules until you publish them.</li>
            <li>Check a file against each platform&rsquo;s published limits before publishing, and
              tell you in plain language when it will be rejected.</li>
            <li>Publish your posts to the accounts you selected, at the time you chose.</li>
            <li>Retry a failed delivery, and record why a platform refused it.</li>
            <li>Show you the status, platform identifier and link for each delivery.</li>
            <li>Diagnose faults, prevent abuse and keep the service reliable and secure.</li>
            <li>Send service messages such as delivery failures, security alerts and policy notices.</li>
            <li>Comply with legal, tax, regulatory and business obligations.</li>
          </ul>
          <p>
            We do not sell personal information. We do not use your content or platform data to
            train machine-learning models. We do not use it for advertising or to build advertising
            profiles.
          </p>
        </Sec>

        <Sec id="meta" n={7} title="Facebook and Instagram Data">
          <p>
            Our use of information received from Meta APIs adheres to the{" "}
            <a href="https://developers.facebook.com/terms/" target="_blank" rel="noreferrer noopener">Meta Platform Terms</a>{" "}
            and{" "}
            <a href="https://developers.facebook.com/devpolicy/" target="_blank" rel="noreferrer noopener">Developer Policies</a>,
            including the limited-use requirements.
          </p>
          <p>We request only the permissions the publishing feature needs:</p>
          <ul>
            <li><code>pages_show_list</code> — to list the Pages you administer so you can choose one.</li>
            <li><code>pages_read_engagement</code> — to read a Page&rsquo;s name and picture, and to obtain the Page access token used for publishing.</li>
            <li><code>pages_manage_posts</code> — to create the photo or video post you composed.</li>
            <li><code>instagram_basic</code> — to identify the Instagram Business account linked to your Page.</li>
            <li><code>instagram_content_publish</code> — to publish the media container you composed to that account.</li>
          </ul>
          <p>
            We do not transfer Meta platform data to data brokers, advertising networks or
            monetisation partners. We do not combine it with data from other sources to build
            profiles. We do not use it for any purpose other than operating the features you asked
            for.
          </p>
          <p>
            If you remove {COMPANY.product} from your Facebook Business Integrations, the token
            becomes invalid immediately and any pending posts to that account will fail rather than
            publish.
          </p>
        </Sec>

        <Sec id="youtube" n={8} title="YouTube API Services and Google Data">
          <p>
            {COMPANY.product} uses YouTube API Services. By connecting a YouTube channel you agree
            to be bound by the{" "}
            <a href={PLATFORM_LINKS.youtubeTerms} target="_blank" rel="noreferrer noopener">YouTube Terms of Service</a>.
            Google&rsquo;s{" "}
            <a href={PLATFORM_LINKS.googlePrivacy} target="_blank" rel="noreferrer noopener">Privacy Policy</a>{" "}
            describes how Google handles data it receives.
          </p>
          <p>We request two Google scopes:</p>
          <ul>
            <li><code>youtube.upload</code> — to upload the video you composed to the channel you selected.</li>
            <li><code>youtube.readonly</code> — to list your channels so you can choose one, and to read the title and thumbnail we display.</li>
          </ul>
          <p>
            {COMPANY.product}&rsquo;s use and transfer of information received from Google APIs
            adheres to the{" "}
            <a href={PLATFORM_LINKS.googleLimitedUse} target="_blank" rel="noreferrer noopener">Google API Services User Data Policy</a>,
            including the Limited Use requirements. We store your channel identifier, channel title,
            channel thumbnail, an encrypted refresh token and the identifier of each video we upload
            for you. We store nothing else from your Google account.
          </p>
          <p>
            <strong>Retention and deletion:</strong> we keep Google user data only for as long as the
            channel is connected and your account is active, and we delete or destroy it when that
            period ends. Section 9 sets out, item by item, how long each piece of Google user data is
            kept and every way you can have it deleted.
          </p>
          <p>
            <strong>You can revoke our access to your YouTube data at any time</strong> from the{" "}
            <a href={PLATFORM_LINKS.googlePermissions} target="_blank" rel="noreferrer noopener">Google security settings page</a>{" "}
            at myaccount.google.com/permissions, or by disconnecting the channel inside{" "}
            {COMPANY.product}. To request deletion of the YouTube data we hold, follow the steps in{" "}
            <Link to="/data-deletion">Delete your data</Link>.
          </p>
        </Sec>

        <Sec id="google-retention" n={9} title="Retention and Deletion of Google User Data">
          <p>
            This section states, specifically and separately from the general retention rules in
            section 13, how long {COMPANY.product} keeps Google user data and how that data is
            deleted. &ldquo;Google user data&rdquo; here means everything we obtain from your Google
            account through the YouTube API Services scopes listed in section 8.
          </p>
          <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Google user data we hold</th>
                <th>How long we keep it</th>
                <th>How it is deleted</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>OAuth refresh token and access token</td>
                <td>Only while the channel is connected to your workspace.</td>
                <td>Deleted from our database immediately and permanently when you disconnect the
                  channel, and within 7 days of an account-deletion request.</td>
              </tr>
              <tr>
                <td>Channel identifier, channel title and channel thumbnail URL</td>
                <td>Only while the channel is connected to your workspace.</td>
                <td>Deleted with the connection record when you disconnect the channel, and within
                  30 days of an account-deletion request.</td>
              </tr>
              <tr>
                <td>Identifier and link of each video we uploaded for you, and the delivery status
                  of that upload</td>
                <td>While your account is active, so you have a history of what was published where.</td>
                <td>Deleted as soon as you delete the post inside {COMPANY.product}, and within 30
                  days of an account-deletion request.</td>
              </tr>
              <tr>
                <td>Encrypted infrastructure backups containing any of the above</td>
                <td>A rolling window of no more than 90 days.</td>
                <td>Overwritten automatically on that rolling schedule. Backups are not readable by
                  the application and are never used to restore deleted accounts.</td>
              </tr>
            </tbody>
          </table>
          </div>
          <p>
            We hold no other Google user data. We do not copy your existing videos, playlists,
            comments, subscribers, analytics, Gmail, Contacts, Drive files or Google profile data,
            and we do not retain a copy of a video after Google has accepted the upload — the file
            is sent from your browser or mobile app straight to Google&rsquo;s upload endpoint.
          </p>
          <p>
            <strong>When the retention period for a given type of Google user data expires, we
            delete or destroy that data.</strong> We retain Google user data only for the length of
            time needed to provide the publishing features described in section 6, unless a longer
            period is required or permitted by law. Where a limited record must be kept to comply
            with a legal, tax or accounting obligation, to resolve a dispute or to prevent fraud and
            abuse, it is kept only for as long as that purpose requires, is not used for any other
            purpose, and is deleted or destroyed when the purpose ends.
          </p>
          <p><strong>You can have your Google user data deleted in any of these ways:</strong></p>
          <ul>
            <li><strong>Disconnect the channel in {COMPANY.product}</strong> — open Connections,
              choose the YouTube channel and select Disconnect. The stored token and channel record
              are deleted at once.</li>
            <li><strong>Delete individual posts</strong> — deleting a post removes the video
              identifier, link and delivery record we hold for it.</li>
            <li><strong>Request full deletion by email</strong> — write to{" "}
              <EmailLink address={COMPANY.privacyEmail} /> from your registered address with the
              subject &ldquo;Delete my account&rdquo;. We action verified requests on the timeline
              above and confirm in writing when it is done. Step-by-step instructions are on the{" "}
              <Link to="/data-deletion">Delete your data</Link> page.</li>
            <li><strong>Revoke at Google</strong> — remove {COMPANY.product} at{" "}
              <a href={PLATFORM_LINKS.googlePermissions} target="_blank" rel="noreferrer noopener">myaccount.google.com/permissions</a>.
              This invalidates our token instantly. To also erase the connection record we hold, use
              one of the routes above.</li>
          </ul>
          <p>
            Revoking access or deleting your {COMPANY.product} data does not remove videos already
            published to your YouTube channel. Those belong to your channel and must be deleted on
            YouTube.
          </p>
        </Sec>

        <Sec id="cookies" n={10} title="Cookies and Local Storage">
          <p>
            We use browser local storage to keep your sign-in session so you are not asked to sign in
            on every page. This is required for the service to function and is not used for
            advertising or cross-site tracking.
          </p>
          <p>
            We do not embed third-party advertising trackers, social plugins or cross-site
            behavioural profiling scripts.
          </p>
        </Sec>

        <Sec id="sharing" n={11} title="Sharing of Information">
          <p>We share limited information only where it is needed to run the service:</p>
          <ul>
            <li><strong>With the social platforms you connect,</strong> when you publish — the caption,
              title, description, privacy setting and the media itself. This is the whole point of
              the service and happens only for accounts you selected.</li>
            <li><strong>With other members of your workspace,</strong> who can see the workspace&rsquo;s
              posts, media and connected accounts. They cannot see your credentials.</li>
            <li><strong>With the service providers listed in section 12,</strong> who host and operate
              our infrastructure under contract.</li>
            <li><strong>With regulators, law enforcement or courts,</strong> where required by law.</li>
            <li><strong>In a business transfer,</strong> if the company or the product is merged or
              acquired, subject to this policy continuing to apply.</li>
          </ul>
          <p>We do not sell or rent personal information to third parties.</p>
        </Sec>

        <Sec id="processors" n={12} title="Service Providers and Hosting">
          <p>We rely on a small set of infrastructure providers:</p>
          <ul>
            <li><strong>Supabase</strong> — authentication, the application database and private media
              storage.</li>
            <li><strong>Cloudflare</strong> — website delivery and the scheduler that triggers posts at
              their appointed time.</li>
            <li><strong>Meta Platforms, Inc. and Google LLC</strong> — the destination platforms, which
              receive only what you chose to publish.</li>
          </ul>
          <p>
            These providers process data on our instructions and are not permitted to use it for
            their own purposes.
          </p>
        </Sec>

        <Sec id="retention" n={13} title="Data Retention">
          <p>
            We retain personal information — including Google user data and Meta platform data — only
            for the length of time needed to fulfil the purposes set out in section 6, unless a longer
            retention period is required or permitted by law. <strong>When the retention period for a
            given type of data expires, we delete or destroy it.</strong>
          </p>
          <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Category</th>
                <th>Retention period</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Account, workspace and membership records</td>
                <td>While your account is active. Deleted within 30 days of a verified deletion request.</td>
              </tr>
              <tr>
                <td>Posts, captions, schedules and delivery records</td>
                <td>While your account is active, or until you delete the post. Deleted within 30 days
                  of a verified deletion request.</td>
              </tr>
              <tr>
                <td>Uploaded photos and videos</td>
                <td>While your account is active, or until you delete the media item. Deleted within
                  30 days of a verified deletion request.</td>
              </tr>
              <tr>
                <td>Connected-account records and encrypted access or refresh tokens (Facebook,
                  Instagram, Google)</td>
                <td>Only while the account is connected. Deleted immediately on disconnect, and within
                  7 days of a verified deletion request.</td>
              </tr>
              <tr>
                <td>Signed media links issued to Meta for publishing</td>
                <td>Expire automatically, two hours by default. Not reissued and not stored.</td>
              </tr>
              <tr>
                <td>Diagnostic and error records</td>
                <td>Up to 90 days, then purged automatically.</td>
              </tr>
              <tr>
                <td>Support correspondence</td>
                <td>Up to 24 months from the close of the request, then deleted.</td>
              </tr>
              <tr>
                <td>Encrypted infrastructure backups</td>
                <td>A rolling window of no more than 90 days, then overwritten.</td>
              </tr>
            </tbody>
          </table>
          </div>
          <p>
            Section 9 states the retention and deletion rules for Google user data specifically. When
            you disconnect a social account, the stored credential for it is deleted at once. When you
            delete your account, we delete your posts, media, connections and credentials as described
            in <Link to="/data-deletion">Delete your data</Link>, and confirm in writing when it is
            done.
          </p>
          <p>
            Limited records may be retained beyond the periods above only where necessary to resolve
            disputes, prevent fraud or abuse, enforce our agreements, or meet legal, tax and accounting
            obligations. Those records are kept only as long as that purpose requires, are not used for
            anything else, and are deleted or destroyed when the purpose ends. Content already published
            to Facebook, Instagram or YouTube lives on those platforms and must be deleted there.
          </p>
        </Sec>

        <Sec id="deletion" n={14} title="Deleting Your Data">
          <p>
            You can request deletion of your data at any time. There are three routes, all described
            step by step on the <Link to="/data-deletion">Delete your data</Link> page:
          </p>
          <ul>
            <li><strong>Self-service:</strong> disconnect an account in{" "}
              {COMPANY.product} to delete its stored credential, or delete individual posts and media.</li>
            <li><strong>Full account deletion:</strong> email{" "}
              <EmailLink address={COMPANY.privacyEmail} />{" "}
              from your registered address with the subject &ldquo;Delete my account&rdquo;. We
              action verified requests within 30 days and confirm by email when it is done.</li>
            <li><strong>Revoke at the platform:</strong> remove {COMPANY.product} from your{" "}
              <a href={PLATFORM_LINKS.metaAppSettings} target="_blank" rel="noreferrer noopener">Facebook Business Integrations</a>{" "}
              or your{" "}
              <a href={PLATFORM_LINKS.googlePermissions} target="_blank" rel="noreferrer noopener">Google security settings</a>.</li>
          </ul>
        </Sec>

        <Sec id="rights" n={15} title="Your Rights">
          <p>
            Subject to applicable law, including India&rsquo;s Digital Personal Data Protection Act,
            2023, you may request access to your personal data, correction of inaccurate data,
            deletion, withdrawal of consent, and grievance redressal.
          </p>
          <p>
            To exercise these rights, contact{" "}
            <EmailLink address={COMPANY.privacyEmail} />. We may ask you to
            verify control of your registered email address before acting on a request.
          </p>
        </Sec>

        <Sec id="security" n={16} title="Security">
          <p>
            We use administrative, technical and organisational safeguards including encrypted
            transmission, encryption of credentials at rest, row-level access control that scopes
            every record to its workspace, private storage buckets, short-lived signed links, and
            separation of the credential store from the public API. Our{" "}
            <Link to="/security">Security</Link> page describes these in more detail.
          </p>
          <p>
            No internet service can guarantee complete security. Protect your email account, since
            it is what signs you in, and disconnect social accounts you no longer use.
          </p>
        </Sec>

        <Sec id="transfers" n={17} title="International Transfers">
          <p>
            Our infrastructure providers operate globally, so your information may be processed
            outside your country of residence. Where required, we rely on appropriate safeguards and
            on the contractual commitments those providers make about how they handle data on our
            behalf.
          </p>
        </Sec>

        <Sec id="children" n={18} title="Children">
          <p>
            {COMPANY.product} is intended for adults operating business or creator accounts. It is
            not directed at children, and we do not knowingly collect personal information from
            anyone under 18. If you believe a minor has provided us information, contact{" "}
            <EmailLink address={COMPANY.privacyEmail} /> and we will delete it.
          </p>
        </Sec>

        <Sec id="changes" n={19} title="Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. The updated policy will be posted on
            this page with a revised Last Updated date. Where changes are material we will also
            notify you by email or in the application.
          </p>
        </Sec>

        <Sec id="contact" n={20} title="Contact Us">
          <p className="contact-block">
            {COMPANY.product}<br />
            {COMPANY.legalName}<br />
            {COMPANY.address.oneLine}<br />
            Email: <EmailLink address={COMPANY.supportEmail} /><br />
            Phone: <a href={`tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`}>{COMPANY.phone}</a>
          </p>
        </Sec>

        <Sec id="grievance" n={21} title="Grievance Redressal">
          <p>
            For privacy complaints, access requests, correction requests, deletion requests or other
            data-related queries, contact the Grievance Officer at {COMPANY.legalName}.
          </p>
          <p className="contact-block">
            Email: <EmailLink address={COMPANY.privacyEmail} /><br />
            Phone: <a href={`tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`}>{COMPANY.phone}</a>
          </p>
          <p className="muted">
            This Privacy Policy is governed by the laws of India. Disputes will be subject to the
            jurisdiction of competent courts in {COMPANY.jurisdiction}.
          </p>
        </Sec>
      </LegalPage>
    </div>
  );
}
